import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchLabBookingChartData,
  fetchAssociateMapping,
  fetchEmployeesByFacility,
} from "../../API_Service/LabBooking_Slice";
import { fetchDropdownData } from "../../API_Service/AssociateShiftMapping_Slice";
import { companyID } from "../../Common/UIComponents";

// ─── Constants ────────────────────────────────────────────────────────────────
const HOURS_START = 0;
const HOURS_END = 24;
const TOTAL_HOURS = HOURS_END - HOURS_START;

const STATUS_COLORS = {
  TENB: {
    bg: "#fef3c7",
    border: "#d97706",
    text: "#78350f",
    label: "Tentative",
  },
  APPR: { bg: "#d1fae5", border: "#059669", text: "#065f46", label: "Booked" },
  CONF: {
    bg: "#dbeafe",
    border: "#2563eb",
    text: "#1e3a8a",
    label: "Confirmed",
  },
  CMPL: {
    bg: "#fce7f3",
    border: "#db2777",
    text: "#831843",
    label: "Completed",
  },
  CANC: {
    bg: "#fee2e2",
    border: "#dc2626",
    text: "#991b1b",
    label: "Cancelled",
  },
  DEFAULT: {
    bg: "#e5e7eb",
    border: "#9ca3af",
    text: "#374151",
    label: "Booked",
  },
};

const HOLIDAY_COLOR = { bg: "#fff5f5", border: "#f87171", text: "#b91c1c" };
const TEAL = "#08757c";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isWeekend(date) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

function isHoliday(date, holidays) {
  const s = toDateStr(date);
  return holidays.some((h) => {
    const from = h.HolidayFromDate.split("T")[0];
    const to = h.HolidayToDate ? h.HolidayToDate.split("T")[0] : from;
    return s >= from && s <= to;
  });
}

function getHolidayName(date, holidays) {
  const s = toDateStr(date);
  const h = holidays.find((h) => {
    const from = h.HolidayFromDate.split("T")[0];
    const to = h.HolidayToDate ? h.HolidayToDate.split("T")[0] : from;
    return s >= from && s <= to;
  });
  return h?.HolidayName || "";
}

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDateStr(isoString) {
  const [y, m, d] = isoString.split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, d);
}

function parseLocalISO(isoString) {
  const [datePart, timePart = "00:00:00"] = isoString.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi, sRaw = "0"] = timePart.split(":");
  const s = parseFloat(sRaw);
  return new Date(y, mo - 1, d, Number(h), Number(mi), Math.floor(s), 0);
}

// FIX 1: slot 24 means midnight of next day — clamp to 23:59:59 to keep end within same date
function slotToDateTime(slot, date) {
  if (slot >= TOTAL_HOURS) {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      0,
    );
  }
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    slot,
    0,
    0,
    0,
  );
}

function toLocalISOString(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

function formatAMPM(date) {
  const h = date.getHours();
  const min = String(date.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(h12).padStart(2, "0")}:${min} ${ampm}`;
}

function hourLabel(h) {
  if (h === 0) return "12am";
  if (h === 12) return "12pm";
  if (h === 24) return "12am";
  return h > 12 ? `${h - 12}pm` : `${h}am`;
}

function fmtDate(d) {
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${DAYS[d.getDay()]}, ${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtDateShort(d) {
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
}

function fmtDateFull(d) {
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtDateTime(d) {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = String(d.getDate()).padStart(2, "0");
  const mon = months[d.getMonth()];
  const yr = d.getFullYear();
  const h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${day} ${mon} ${yr} ${String(h12).padStart(2, "0")}:${min} ${ampm}`;
}

function fmtDayHeader(d) {
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${DAYS[d.getDay()]} ${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
}

function fmtMonthYear(d) {
  const MONTHS_LONG = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
}

function getWeekdays(startDate, count, workWeek = 5) {
  const result = [];
  const cur = new Date(startDate);
  while (result.length < count) {
    if (workWeek === 7 || !isWeekend(cur)) result.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

function getCurrentCalendarWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  const week = [];
  for (let i = 0; i < 7; i++) {
    week.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return week;
}

function getDurationString(
  startSlot,
  startDate,
  endSlot,
  endDate,
  bookableDays,
  shiftStart,
  shiftEnd,
) {
  if (shiftStart && shiftEnd) {
    const [sh, sm] = shiftStart.split(":").map(Number);
    const [eh, em] = shiftEnd.split(":").map(Number);
    const shiftHours = (eh * 60 + em - (sh * 60 + sm)) / 60;
    const days = bookableDays?.length || 1;
    const totalHours = Math.round(shiftHours * days * 10) / 10;
    const dayLabel = days === 1 ? "1 day" : `${days} days`;
    const wholeDays = Math.floor(totalHours / 24);
    const rem = Math.round(totalHours % 24);
    if (wholeDays > 0 && rem > 0) return `${wholeDays}d ${rem}h (${dayLabel})`;
    if (wholeDays > 0) return `${wholeDays}d (${dayLabel})`;
    return `${days}d (${dayLabel})`;
  }
  if (bookableDays && bookableDays.length > 0) {
    const hoursPerDay = endSlot - startSlot + 1;
    const totalHours = bookableDays.length * hoursPerDay;
    const days = Math.floor(totalHours / 24);
    const rem = totalHours % 24;
    const dayLabel =
      bookableDays.length === 1 ? "1 day" : `${bookableDays.length} days`;
    if (days > 0 && rem > 0) return `${days}d ${rem}h (${dayLabel})`;
    if (days > 0) return `${days}d (${dayLabel})`;
    return `${totalHours}h`;
  }
  const start = slotToDateTime(startSlot, startDate);
  const endHour = endSlot + 1 >= TOTAL_HOURS ? 24 : endSlot + 1;
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
    endHour,
    0,
    0,
  );
  const hours = (end - start) / 3600000;
  const wholeDays = Math.floor(hours / 24);
  const rem = Math.round(hours % 24);
  if (wholeDays > 0) return `${wholeDays}d ${rem}h`;
  return `${hours}h`;
}

// REPLACE the entire getPlannedHours function:
function getPlannedHours(
  startSlot,
  startDate,
  endSlot,
  endDate,
  bookableDays,
  shiftStart,
  shiftEnd,
) {
  if (shiftStart && shiftEnd) {
    const [sh, sm] = shiftStart.split(":").map(Number);
    const [eh, em] = shiftEnd.split(":").map(Number);
    const shiftHours = (eh * 60 + em - (sh * 60 + sm)) / 60;
    const days = bookableDays?.length || 1;
    return Math.round(shiftHours * days * 10) / 10;
  }
  if (bookableDays && bookableDays.length > 0) {
    return bookableDays.length * (endSlot - startSlot + 1);
  }
  const start = slotToDateTime(startSlot, startDate);
  const endHour = endSlot + 1 >= TOTAL_HOURS ? 24 : endSlot + 1;
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
    endHour,
    0,
    0,
  );
  return Math.round(((end - start) / 3600000) * 10) / 10;
}

function getMonthWeekRows(year, month, workWeek = 5) {
  const rows = [];
  let week = [];
  const d = new Date(year, month, 1);

  if (workWeek === 5) {
    const startDay = d.getDay() === 0 ? 6 : d.getDay() - 1;
    for (let i = 0; i < Math.min(startDay, 5); i++) week.push(null);
    while (d.getMonth() === month) {
      if (isWeekend(d)) {
        d.setDate(d.getDate() + 1);
        continue;
      }
      if (week.length > 0 && d.getDay() === 1) {
        while (week.length < 5) week.push(null);
        rows.push(week);
        week = [];
      }
      week.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    while (week.length < 5) week.push(null);
    if (week.some(Boolean)) rows.push(week);
  } else {
    const startDay = d.getDay() === 0 ? 6 : d.getDay() - 1;
    for (let i = 0; i < startDay; i++) week.push(null);
    while (d.getMonth() === month) {
      if (week.length === 7) {
        rows.push(week);
        week = [];
      }
      week.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    while (week.length < 7) week.push(null);
    if (week.some(Boolean)) rows.push(week);
  }

  return rows;
}

function assignLanes(bookings) {
  const sorted = [...bookings].sort(
    (a, b) =>
      parseLocalISO(a.Planned_Start_Date_Hrs) -
      parseLocalISO(b.Planned_Start_Date_Hrs),
  );
  const laneEndTimes = [];
  return sorted.map((b) => {
    const st = parseLocalISO(b.Planned_Start_Date_Hrs).getTime();
    const et = parseLocalISO(b.Planned_End_Date_Hrs).getTime();
    let lane = laneEndTimes.findIndex((laneEnd) => laneEnd <= st);
    if (lane === -1) lane = laneEndTimes.length;
    laneEndTimes[lane] = et;
    return { ...b, lane };
  });
}

function isEmployeeAvailable(date, employeeShiftData) {
  if (!employeeShiftData || employeeShiftData.length === 0) return false;
  const s = toDateStr(date);
  return employeeShiftData.some((row) => {
    const from = row.FromDate?.split("T")[0];
    const to = row.ToDate?.split("T")[0] || from;
    return s >= from && s <= to;
  });
}

function getEmployeeShiftForDate(date, employeeShiftData) {
  if (!employeeShiftData || employeeShiftData.length === 0) return null;
  const s = toDateStr(date);
  return (
    employeeShiftData.find((row) => {
      const from = row.FromDate?.split("T")[0];
      const to = row.ToDate?.split("T")[0] || from;
      return s >= from && s <= to;
    }) || null
  );
}

function getShiftIcon(shift) {
  const name = shift.Shift_Name?.toLowerCase() || "";
  const start = shift.Shift_Start_Time || "";
  if (
    name.includes("morning") ||
    start.startsWith("06") ||
    start.startsWith("07")
  )
    return "☀️";
  if (
    name.includes("night") ||
    start.startsWith("22") ||
    start.startsWith("23")
  )
    return "🌙";
  return "🕐";
}

function timeStrToHour(timeStr) {
  if (!timeStr) return null;
  const [hStr] = timeStr.split(":");
  return parseInt(hStr, 10);
}

function shiftToSlots(shift) {
  const s = timeStrToHour(shift?.Shift_Start_Time);
  const e = timeStrToHour(shift?.Shift_End_Time);
  if (s === null || e === null) return null;
  const endSlot = e === 0 ? 23 : e <= s ? e + 24 - 1 : e - 1;
  return { startSlot: s, endSlot: Math.min(endSlot, TOTAL_HOURS - 1) };
}

// ─── Day View ─────────────────────────────────────────────────────────────────
function DayView({
  date,
  bookings,
  holidays,
  daySelStep,
  daySelStart,
  daySelEnd,
  onDaySlotClick,
  workWeek,
  activeShiftSlots,
  isEmployeeMode,
  employeeShiftData,
}) {
  const holiday = isHoliday(date, holidays);
  const weekend = workWeek === 5 && isWeekend(date);
  const empUnavailable =
    isEmployeeMode && !isEmployeeAvailable(date, employeeShiftData);
  const empShiftForDate = isEmployeeMode
    ? getEmployeeShiftForDate(date, employeeShiftData)
    : null;
  const cur = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const isPast = cur < todayMidnight;
  const isDisabledDay = holiday || weekend || empUnavailable || isPast;
  const visibleBookings = isEmployeeMode ? [] : bookings;

  const dayBookings = visibleBookings.filter((b) => {
    const bs = parseDateStr(b.Planned_Start_Date_Hrs);
    const be = parseDateStr(b.Planned_End_Date_Hrs);
    return cur.getTime() >= bs.getTime() && cur.getTime() <= be.getTime();
  });

  const laned = assignLanes(dayBookings);
  const totalLanes =
    laned.length > 0 ? Math.max(...laned.map((b) => b.lane)) + 1 : 1;
  const LANE_HEIGHT = 36;
  const LANE_GAP = 4;
  const TOP_PADDING = 8;
  const rowHeight = Math.max(
    64,
    TOP_PADDING + totalLanes * (LANE_HEIGHT + LANE_GAP) + 8,
  );

  const getCellBooking = (slot) => {
    const cellDt = slotToDateTime(slot, date);
    const cellDtEnd = slotToDateTime(slot + 1, date);
    const matches = visibleBookings.filter((b) => {
      const bs = parseLocalISO(b.Planned_Start_Date_Hrs);
      const be = parseLocalISO(b.Planned_End_Date_Hrs);
      const bsd = parseDateStr(b.Planned_Start_Date_Hrs);
      const bed = parseDateStr(b.Planned_End_Date_Hrs);
      if (cur.getTime() >= bsd.getTime() && cur.getTime() <= bed.getTime()) {
        if (cur.getTime() === bsd.getTime() && cur.getTime() === bed.getTime())
          return cellDt < be && cellDtEnd > bs;
        if (cur.getTime() === bsd.getTime()) return cellDt >= bs;
        if (cur.getTime() === bed.getTime()) return cellDt < be;
        return true;
      }
      return false;
    });
    if (matches.length === 0) return undefined;
    return matches.find((b) => b.Status_Code !== "TENB") ?? matches[0];
  };

  const isCellSelected = (slot) => {
    if (!daySelStart || toDateStr(date) !== toDateStr(daySelStart.date))
      return false;
    const lo = Math.min(
      daySelStart.slot,
      daySelEnd ? daySelEnd.slot : daySelStart.slot,
    );
    const hi = Math.max(
      daySelStart.slot,
      daySelEnd ? daySelEnd.slot : daySelStart.slot,
    );
    return slot >= lo && slot <= hi;
  };

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          flex: 1,
          position: "relative",
          minHeight: rowHeight,
        }}
      >
        {Array.from({ length: TOTAL_HOURS }).map((_, slot) => {
          const cellBooking = getCellBooking(slot);
          const isBookingBlocked =
            cellBooking && !["TENB", "CONF", "APPR"].includes(cellBooking.Status_Code);
          const outsideShift = activeShiftSlots
            ? slot < activeShiftSlots.startSlot ||
              slot > activeShiftSlots.endSlot
            : false;
          const disabled = isDisabledDay || isBookingBlocked || outsideShift;
          const selected = isCellSelected(slot);

          let bg = "transparent";
          let extraStyle = {};
          if (selected) {
            bg = "rgba(8,117,124,0.2)";
          } else if (holiday) {
            bg = HOLIDAY_COLOR.bg;
          } else if (weekend) {
            bg = "#f9fafb";
          } else if (outsideShift) {
            bg =
              "repeating-linear-gradient(135deg,#f3f4f6,#f3f4f6 4px,#e9eaec 4px,#e9eaec 8px)";
            extraStyle = { opacity: 0.7 };
          } else if (isBookingBlocked) {
            bg =
              "repeating-linear-gradient(135deg,#fee2e2,#fee2e2 4px,#fecaca 4px,#fecaca 8px)";
          } else if (cellBooking) {
            bg = (
              STATUS_COLORS[cellBooking.Status_Code] || STATUS_COLORS.DEFAULT
            ).bg;
          }

          return (
            <div
              key={slot}
              onClick={() => !disabled && onDaySlotClick(date, slot)}
              onMouseEnter={(e) => {
                if (!disabled)
                  e.currentTarget.style.outline =
                    "2px solid rgba(8,117,124,0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.outline = "none";
              }}
              title={
                holiday
                  ? getHolidayName(date, holidays)
                  : outsideShift
                    ? "Outside selected shift hours"
                    : isBookingBlocked
                      ? `${cellBooking.Project_Name || "Booked"} — ${(STATUS_COLORS[cellBooking.Status_Code] || STATUS_COLORS.DEFAULT).label} (not available)`
                      : cellBooking
                        ? `${cellBooking.Project_Name || "Booked"} (${STATUS_COLORS[cellBooking.Status_Code]?.label || "Tentative"} — bookable)`
                        : `${hourLabel(slot)} — click to ${!daySelStart ? "set start" : "set end"}`
              }
              style={{
                flex: 1,
                background: bg,
                borderLeft: slot > 0 ? "1px solid #f0f0f0" : "none",
                cursor: disabled ? "not-allowed" : "pointer",
                transition: "background 0.08s",
                position: "relative",
                ...extraStyle,
              }}
            />
          );
        })}

        {!isDisabledDay &&
          laned.map((b, i) => {
            const st = parseLocalISO(b.Planned_Start_Date_Hrs);
            const et = parseLocalISO(b.Planned_End_Date_Hrs);
            const dayStart = new Date(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              0,
              0,
              0,
            );
            const dayEnd = new Date(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              24,
              0,
              0,
            );
            const clampedSt = st < dayStart ? dayStart : st;
            const clampedEt = et > dayEnd ? dayEnd : et;
            const startHourFrac =
              clampedSt.getHours() + clampedSt.getMinutes() / 60;
            const endIsPastMidnight = et > dayEnd;
            const endHourFrac = endIsPastMidnight
              ? TOTAL_HOURS
              : clampedEt.getHours() + clampedEt.getMinutes() / 60;
            const left = (startHourFrac / TOTAL_HOURS) * 100;
            const width = Math.max(
              ((endHourFrac - startHourFrac) / TOTAL_HOURS) * 100,
              0.8,
            );
            const sc = STATUS_COLORS[b.Status_Code] || STATUS_COLORS.DEFAULT;
            const topOffset = TOP_PADDING + b.lane * (LANE_HEIGHT + LANE_GAP);
            const isStartDay =
              cur.getTime() ===
              parseDateStr(b.Planned_Start_Date_Hrs).getTime();
            const isEndDay =
              cur.getTime() === parseDateStr(b.Planned_End_Date_Hrs).getTime();
            const startLabel = isStartDay ? formatAMPM(st) : "← continues";
            const endLabel = isEndDay ? formatAMPM(et) : "continues →";
            const tooltipText = `${b.Project_Name || "Booking"} — ${b.Test_Name || ""} (${sc.label})\n${startLabel} – ${endLabel}`;

            return (
              <div
                key={i}
                title={tooltipText}
                style={{
                  position: "absolute",
                  left: `${left}%`,
                  width: `${width}%`,
                  top: topOffset,
                  height: LANE_HEIGHT,
                  background: sc.bg,
                  border: `1.5px solid ${sc.border}`,
                  borderRadius: 6,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 600,
                  color: sc.text,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  zIndex: 2,
                  padding: "0 6px",
                  gap: 1,
                  boxSizing: "border-box",
                }}
              >
                <span style={{ fontSize: 9, opacity: 0.75 }}>{sc.label}</span>
                <span style={{ fontSize: 9 }}>
                  {startLabel} – {endLabel}
                </span>
                {b.Project_Name && (
                  <span
                    style={{
                      fontSize: 9,
                      opacity: 0.85,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      maxWidth: "100%",
                    }}
                  >
                    {b.Project_Name}
                  </span>
                )}
              </div>
            );
          })}

        {isEmployeeMode && !isDisabledDay && empShiftForDate && (
          <div
            style={{
              position: "absolute",
              top: 2,
              left: `${(timeStrToHour(empShiftForDate.Shift_Start_Time) / TOTAL_HOURS) * 100}%`,
              width: `${((timeStrToHour(empShiftForDate.Shift_End_Time) - timeStrToHour(empShiftForDate.Shift_Start_Time)) / TOTAL_HOURS) * 100}%`,
              height: 4,
              background: TEAL,
              borderRadius: 2,
              opacity: 0.85,
              zIndex: 3,
              pointerEvents: "none",
            }}
          />
        )}

        {(holiday || weekend || empUnavailable) && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: holiday
                ? HOLIDAY_COLOR.bg
                : empUnavailable
                  ? "#f0f0f0"
                  : "#f9fafb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: holiday ? HOLIDAY_COLOR.text : "#9ca3af",
                background: "rgba(255,255,255,0.92)",
                padding: "4px 12px",
                borderRadius: 6,
                border: `1px solid ${holiday ? HOLIDAY_COLOR.border : "#e5e7eb"}`,
              }}
            >
              {holiday
                ? getHolidayName(date, holidays)
                : empUnavailable
                  ? "Not Available"
                  : "Weekend"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Week View Compact Cell ───────────────────────────────────────────────────
function CompactDayCell({
  date,
  bookings,
  holidays,
  weekSelStart,
  weekSelEnd,
  onWeekDayClick,
  workWeek,
  isEmployeeMode,
  employeeShiftData,
}) {
  const holiday = isHoliday(date, holidays);
  const weekend = workWeek === 5 && isWeekend(date);
  const dateStr = toDateStr(date);
  const cur = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const isPast = cur < todayMidnight;
  const empShiftForDate = isEmployeeMode
    ? getEmployeeShiftForDate(date, employeeShiftData)
    : null;
  const empUnavailable =
    isEmployeeMode && !isEmployeeAvailable(date, employeeShiftData);

  const dayBookings = isEmployeeMode
    ? []
    : bookings.filter((b) => {
        const bs = parseDateStr(b.Planned_Start_Date_Hrs);
        const be = parseDateStr(b.Planned_End_Date_Hrs);
        return cur.getTime() >= bs.getTime() && cur.getTime() <= be.getTime();
      });
  const dayHasBlockingBooking = dayBookings.some(
    (b) => !["TENB", "CONF", "APPR", "CANC"].includes(b.Status_Code),
  );

  const selStartStr = weekSelStart ? toDateStr(weekSelStart) : null;
  const selEndStr = weekSelEnd ? toDateStr(weekSelEnd) : null;
  const isSelected =
    selStartStr && selEndStr && dateStr >= selStartStr && dateStr <= selEndStr;
  const isSingle = selStartStr && !selEndStr && dateStr === selStartStr;
  const isToday = toDateStr(new Date()) === dateStr;

  const shiftBarStyle = empShiftForDate
    ? (() => {
        const startHour = timeStrToHour(empShiftForDate.Shift_Start_Time);
        const endHour = timeStrToHour(empShiftForDate.Shift_End_Time);
        if (startHour === null || endHour === null) return null;
        const left = (startHour / TOTAL_HOURS) * 100;
        const width = ((endHour - startHour) / TOTAL_HOURS) * 100;
        return { left: `${left}%`, width: `${width}%` };
      })()
    : null;

  const BOOKING_ITEM_HEIGHT = 22; // Height per booking item
  const BASE_HEIGHT = 80; // Base minimum height
  const calculatedMinHeight = Math.max(
    BASE_HEIGHT,
    BASE_HEIGHT + dayBookings.length * BOOKING_ITEM_HEIGHT,
  );

  return (
    <div
      onClick={() =>
        !holiday &&
        !weekend &&
        !empUnavailable &&
        !dayHasBlockingBooking &&
        !isPast &&
        onWeekDayClick(date)
      }
      style={{
        flex: 1,
        minHeight: calculatedMinHeight,
        borderRight: "1px solid #f0f0f0",
        background:
          isSelected || isSingle
            ? "rgba(8,117,124,0.1)"
            : holiday
              ? HOLIDAY_COLOR.bg
              : weekend
                ? "#f9fafb"
                : isPast
                  ? "#f3f4f6"
                  : empUnavailable
                    ? "#f5f5f5"
                    : "#fff",
        cursor:
          holiday || weekend || empUnavailable || isPast
            ? "not-allowed"
            : "pointer",
        display: "flex",
        flexDirection: "column",
        padding: "6px 4px",
        gap: 3,
        position: "relative",
        transition: "background 0.1s",
        outline: isSelected || isSingle ? `2px solid ${TEAL}44` : "none",
        outlineOffset: -2,
        overflow: "hidden",
      }}
    >
      {shiftBarStyle && !holiday && !weekend && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "#e5e7eb",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: shiftBarStyle.left,
              width: shiftBarStyle.width,
              height: "100%",
              background: TEAL,
              borderRadius: 2,
              opacity: 0.85,
            }}
          />
        </div>
      )}
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textAlign: "center",
          marginBottom: 2,
          marginTop: shiftBarStyle ? 4 : 0,
          color: isToday
            ? TEAL
            : holiday
              ? HOLIDAY_COLOR.text
              : weekend
                ? "#9ca3af"
                : empUnavailable
                  ? "#d1d5db"
                  : "#374151",
          flexShrink: 0,
        }}
      >
        {date.getDate()}
        {(isSelected || isSingle) && (
          <span style={{ color: TEAL, marginLeft: 4 }}>✓</span>
        )}
      </div>

      {holiday && (
        <div
          style={{
            fontSize: 9,
            color: HOLIDAY_COLOR.text,
            fontWeight: 600,
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          🎉 {getHolidayName(date, holidays)}
        </div>
      )}
      {weekend && !holiday && (
        <div style={{ fontSize: 9, color: "#9ca3af", textAlign: "center" }}>
          off
        </div>
      )}
      {isEmployeeMode && !holiday && !weekend && empUnavailable && (
        <div
          style={{
            fontSize: 9,
            color: "#d1d5db",
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Not Available
        </div>
      )}
      {empShiftForDate && !holiday && !weekend && (
        <div
          style={{
            fontSize: 8,
            color: TEAL,
            textAlign: "center",
            fontWeight: 600,
            opacity: 0.85,
          }}
        >
          {empShiftForDate.Shift_Start_Time?.slice(0, 5)}–
          {empShiftForDate.Shift_End_Time?.slice(0, 5)}
        </div>
      )}

      {!holiday &&
        !weekend &&
        !isEmployeeMode &&
        dayBookings.map((b, i) => {
          const sc = STATUS_COLORS[b.Status_Code] || STATUS_COLORS.DEFAULT;
          const st = parseLocalISO(b.Planned_Start_Date_Hrs);
          const et = parseLocalISO(b.Planned_End_Date_Hrs);
          return (
            <div
              key={i}
              title={`${b.Project_Name || "Booking"} — ${b.Test_Name || ""} (${sc.label})\n${formatAMPM(st)} – ${formatAMPM(et)}`}
              style={{
                background: sc.bg,
                border: `1px solid ${sc.border}`,
                borderRadius: 4,
                padding: "2px 4px",
                fontSize: 9,
                fontWeight: 600,
                color: sc.text,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {formatAMPM(st)} {b.Project_Name || sc.label}
            </div>
          );
        })}

      {isToday && (
        <div
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: TEAL,
          }}
        />
      )}
    </div>
  );
}

// ─── Month Calendar Cell ──────────────────────────────────────────────────────
function MonthDayCell({
  date,
  bookings,
  holidays,
  selStart,
  selEnd,
  onClick,
  isEmployeeMode,
  employeeShiftData,
}) {
  const holiday = isHoliday(date, holidays);
  const dateStr = toDateStr(date);
  const todayStr = toDateStr(new Date());
  const isToday = dateStr === todayStr;
  const empShiftForDate = isEmployeeMode
    ? getEmployeeShiftForDate(date, employeeShiftData)
    : null;
  const empUnavailable =
    isEmployeeMode && !isEmployeeAvailable(date, employeeShiftData);
  const cur = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const isPast = cur < todayMidnight;

  const dayBookings = !isEmployeeMode
    ? bookings.filter((b) => {
        const bs = parseDateStr(b.Planned_Start_Date_Hrs);
        const be = parseDateStr(b.Planned_End_Date_Hrs);
        return cur.getTime() >= bs.getTime() && cur.getTime() <= be.getTime();
      })
    : [];

  const dayHasBlockingBooking = dayBookings.some(
    (b) => !["TENB", "CONF", "APPR", "CANC"].includes(b.Status_Code),
  );
  const selStartStr = selStart ? toDateStr(selStart) : null;
  const selEndStr = selEnd ? toDateStr(selEnd) : null;
  const isSel =
    selStartStr && selEndStr && dateStr >= selStartStr && dateStr <= selEndStr;
  const isSingle = selStartStr && !selEndStr && dateStr === selStartStr;

  const shiftStart = empShiftForDate
    ? timeStrToHour(empShiftForDate.Shift_Start_Time)
    : null;
  const shiftEnd = empShiftForDate
    ? timeStrToHour(empShiftForDate.Shift_End_Time)
    : null;
  const shiftDuration =
    shiftStart !== null && shiftEnd !== null ? shiftEnd - shiftStart : 0;

  return (
    <div
      onClick={() =>
        !holiday &&
        !empUnavailable &&
        !dayHasBlockingBooking &&
        !isPast &&
        onClick(date)
      }
      style={{
        minHeight: 100,
        borderRight: "1px solid #f0f0f0",
        borderBottom: "1px solid #f0f0f0",
        background: holiday
          ? HOLIDAY_COLOR.bg
          : isSel || isSingle
            ? "rgba(34,197,94,0.08)"
            : isPast
              ? "#f3f4f6"
              : empUnavailable
                ? "#f8f8f8"
                : "#fff",
        cursor: holiday || empUnavailable || isPast ? "not-allowed" : "pointer",
        display: "flex",
        flexDirection: "column",
        padding: "7px 6px 6px",
        gap: 2,
        position: "relative",
        transition: "background 0.1s",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 2,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: isToday ? 700 : 500,
            lineHeight: 1,
            color: isToday
              ? "#fff"
              : holiday
                ? HOLIDAY_COLOR.text
                : empUnavailable
                  ? "#d1d5db"
                  : "#111827",
            ...(isToday && {
              background: TEAL,
              borderRadius: "50%",
              width: 22,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
            }),
          }}
        >
          {date.getDate()}
        </span>
        {(isSel || isSingle) && (
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#22c55e",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ✓
          </span>
        )}
      </div>

      {holiday && (
        <div
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: HOLIDAY_COLOR.text,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flexShrink: 0,
          }}
        >
          🎉 {getHolidayName(date, holidays)}
        </div>
      )}

      {isEmployeeMode &&
        !holiday &&
        (empShiftForDate ? (
          <div
            style={{
              marginTop: 2,
              background: `${TEAL}10`,
              border: `1px solid ${TEAL}30`,
              borderRadius: 6,
              padding: "4px 6px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <div
              style={{
                position: "relative",
                height: 4,
                background: "#e5e7eb",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: `${(shiftStart / TOTAL_HOURS) * 100}%`,
                  width: `${(shiftDuration / TOTAL_HOURS) * 100}%`,
                  height: "100%",
                  background: TEAL,
                  borderRadius: 2,
                }}
              />
            </div>
            <span
              style={{
                fontSize: 8,
                fontWeight: 700,
                color: TEAL,
                letterSpacing: "0.02em",
              }}
            >
              {empShiftForDate.Shift_Start_Time?.slice(0, 5)} –{" "}
              {empShiftForDate.Shift_End_Time?.slice(0, 5)}
            </span>
          </div>
        ) : (
          <div
            style={{
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 4,
              opacity: 0.5,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#d1d5db",
              }}
            />
            <span style={{ fontSize: 8, color: "#9ca3af", fontWeight: 600 }}>
              N/A
            </span>
          </div>
        ))}

      {!isEmployeeMode && !holiday && dayBookings.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: dayBookings.length > 2 ? "auto" : "visible",
            maxHeight: dayBookings.length > 2 ? 58 : "unset",
            scrollbarWidth: "thin",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {dayBookings.map((b, i) => {
            const sc = STATUS_COLORS[b.Status_Code] || STATUS_COLORS.DEFAULT;
            const st = parseLocalISO(b.Planned_Start_Date_Hrs);
            return (
              <div
                key={i}
                style={{
                  background: sc.bg,
                  border: `1px solid ${sc.border}`,
                  borderRadius: 4,
                  padding: "2px 5px",
                  fontSize: 9,
                  fontWeight: 600,
                  color: sc.text,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  lineHeight: 1.5,
                  flexShrink: 0,
                }}
              >
                {formatAMPM(st)} {b.Project_Name || sc.label}
              </div>
            );
          })}
        </div>
      )}
      {!isEmployeeMode && !holiday && dayBookings.length > 2 && (
        <div
          style={{
            fontSize: 8,
            fontWeight: 700,
            color: TEAL,
            background: `${TEAL}15`,
            borderRadius: 3,
            padding: "1px 4px",
            alignSelf: "flex-start",
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          {dayBookings.length} bookings
        </div>
      )}
    </div>
  );
}

// ─── Month View ───────────────────────────────────────────────────────────────
// FIX 4: Accept externalSelStart/externalSelEnd so Full Month button can drive the calendar highlight
function MonthView({
  currentDate,
  bookings,
  holidays,
  onRangeSelect,
  workWeek,
  isEmployeeMode,
  employeeShiftData,
  externalSelStart,
  externalSelEnd,
}) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const [step, setStep] = useState("start");

  useEffect(() => {
    setSelStart(null);
    setSelEnd(null);
    setStep("start");
  }, [year, month]);

  useEffect(() => {
    if (externalSelStart) {
      setSelStart(externalSelStart);
      setSelEnd(externalSelEnd || null);
      setStep("start");
    } else {
      setSelStart(null);
      setSelEnd(null);
      setStep("start");
    }
  }, [externalSelStart?.getTime(), externalSelEnd?.getTime()]);

  useEffect(() => {
    if (selStart && selEnd) {
      const bookableDays = [];
      const cur = new Date(selStart);
      while (cur <= selEnd) {
        const isDisabled =
          (workWeek === 5 && isWeekend(cur)) || isHoliday(cur, holidays);
        if (!isDisabled) bookableDays.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
      }
      onRangeSelect({
        startDate: selStart,
        endDate: selEnd,
        startSlot: 0,
        endSlot: TOTAL_HOURS - 1,
        bookableDays,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selStart?.getTime(), selEnd?.getTime(), workWeek]);

  const weekRows = getMonthWeekRows(year, month, workWeek);

  const handleDayClick = (date) => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    if (
      new Date(date.getFullYear(), date.getMonth(), date.getDate()) <
      todayMidnight
    )
      return;
    if (step === "start") {
      setSelStart(date);
      setSelEnd(null);
      setStep("end");
    } else {
      let start = selStart;
      let end = date;
      if (end < start) [start, end] = [end, start];
      setSelStart(start);
      setSelEnd(end);
      setStep("start");
    }
  };

  const DOW_HEADERS =
    workWeek === 7
      ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
      : ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const cols = workWeek === 7 ? 7 : 5;

  return (
    <div>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            background: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          {DOW_HEADERS.map((d) => (
            <div
              key={d}
              style={{
                padding: "9px 10px",
                fontSize: 11,
                fontWeight: 600,
                color: "#6b7280",
                textAlign: "center",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                borderRight: "1px solid #f0f0f0",
              }}
            >
              {d}
            </div>
          ))}
        </div>
        {weekRows.map((week, wi) => (
          <div
            key={wi}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
            }}
          >
            {week.map((d, di) =>
              d ? (
                <MonthDayCell
                  key={di}
                  date={d}
                  bookings={bookings}
                  holidays={holidays}
                  selStart={selStart}
                  selEnd={selEnd}
                  onClick={handleDayClick}
                  isEmployeeMode={isEmployeeMode}
                  employeeShiftData={employeeShiftData}
                />
              ) : (
                <div
                  key={di}
                  style={{
                    minHeight: 100,
                    background: "#fafafa",
                    borderRight: "1px solid #f0f0f0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                />
              ),
            )}
          </div>
        ))}
      </div>
      {!selStart && (
        <p
          style={{
            fontSize: 12,
            color: "#9ca3af",
            marginTop: 10,
            textAlign: "center",
          }}
        >
          Click a day to set start date
        </p>
      )}
      {selStart && !selEnd && (
        <p
          style={{
            fontSize: 12,
            color: TEAL,
            marginTop: 10,
            textAlign: "center",
          }}
        >
          Start: <strong>{fmtDateFull(selStart)}</strong> — now click an end
          date
        </p>
      )}
      {selStart && selEnd && (
        <div
          style={{
            marginTop: 14,
            background: "#f9fafb",
            border: `1px solid ${TEAL}33`,
            borderLeft: `3px solid ${TEAL}`,
            borderRadius: 10,
            padding: "14px 20px",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              color: "#9ca3af",
              marginBottom: 4,
            }}
          >
            Selected Range
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
            {fmtDateFull(selStart)} → {fmtDateFull(selEnd)}
          </div>
        </div>
      )}

      {/* Legend */}
      <div
        style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}
      >
        {[
          {
            bg: STATUS_COLORS.APPR.bg,
            border: STATUS_COLORS.APPR.border,
            label: "Booked",
          },
          {
            bg: STATUS_COLORS.TENB.bg,
            border: STATUS_COLORS.TENB.border,
            label: "Pending Approval",
          },
          {
            bg: STATUS_COLORS.CANC.bg,
            border: STATUS_COLORS.CANC.border,
            label: "Rejected",
          },
          {
            bg: STATUS_COLORS.CMPL.bg,
            border: STATUS_COLORS.CMPL.border,
            label: "Completed",
          },
          { bg: "rgba(8,117,124,0.15)", border: TEAL, label: "Selected" },
        ].map(({ bg, border, label }) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              color: "#6b7280",
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: bg,
                border: `1.5px solid ${border}`,
              }}
            />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LabBookingGantt({
  onBookingSelect,
  selectedLab,
  locationId,
  onPreviewChange,
}) {
  const dispatch = useDispatch();
  const RESOURCES = useMemo(
    () => (selectedLab?.facilityName ? [selectedLab.facilityName] : []),
    [selectedLab?.facilityName],
  );

  const [viewMode, setViewMode] = useState("day");
  const [currentDate, setCurrentDate] = useState(() => {
    const t = new Date();
    while (isWeekend(t)) t.setDate(t.getDate() + 1);
    return t;
  });

  const [workWeek, setWorkWeek] = useState(5);
  const [selection, setSelection] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);

  const activeShiftSlots = selectedShift ? shiftToSlots(selectedShift) : null;
  const [formErrors, setFormErrors] = useState({});

  const [monthViewDate, setMonthViewDate] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  const [weekSelStart, setWeekSelStart] = useState(null);
  const [weekSelEnd, setWeekSelEnd] = useState(null);
  const [weekSelStep, setWeekSelStep] = useState("start");

  const [daySelStart, setDaySelStart] = useState(null);
  const [daySelEnd, setDaySelEnd] = useState(null);
  const [daySelStep, setDaySelStep] = useState("start");
  const [employeeShiftData, setEmployeeShiftData] = useState([]);
  const bookingPreviewRef = useRef(null);
  const dateInputRef = useRef(null);

  // FIX 4: external month selection state so Full Month button drives calendar highlight
  const [monthSelStart, setMonthSelStart] = useState(null);
  const [monthSelEnd, setMonthSelEnd] = useState(null);
  const [selectionSource, setSelectionSource] = useState(null);

  const isEmployeeMode = !!selectedEmployee && employeeShiftData.length > 0;
  const displayDates =
    viewMode === "month"
      ? []
      : viewMode === "week"
        ? getCurrentCalendarWeek(currentDate)
        : [currentDate];

  // FIX 3: check if current day is a holiday or weekend (used to hide shift pills + Full Day button)
  const isCurrentDayHoliday =
    viewMode === "day" &&
    (isHoliday(currentDate, holidays) ||
      (workWeek === 5 && isWeekend(currentDate)));

  // ─── Fetch lab booking data ────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedLab?.facilityId) return;
    setLoading(true);
    dispatch(
      fetchLabBookingChartData({
        companyID,
        facilityId: selectedLab.facilityId,
        locationId: locationId || 0,
      }),
    )
      .then((res) => {
        const data = res?.payload?.data?.data;
        if (data) {
          setHolidays(data.holidays || []);
          setBookings(data.bookings || []);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedLab, locationId, dispatch]);

  // ─── Fetch employee shift data when employee is selected ──────────────────
  useEffect(() => {
    if (!selectedEmployee || !selectedLab?.facilityId) return;

    dispatch(
      fetchLabBookingChartData({
        companyID,
        facilityId: selectedLab.facilityId,
        locationId: locationId || 0,
        empId: selectedEmployee.value,
      }),
    )
      .then((res) => {
        const payload = res?.payload?.data;
        if (payload?.type === "employeeShift") {
          const rawShifts = payload?.data?.employeeShifts || [];
          setEmployeeShiftData(rawShifts);

          const seen = new Set();
          const mappedShifts = rawShifts.reduce((acc, s, idx) => {
            const key = `${s.Shift_Name?.trim()}_${s.Shift_Start_Time}_${s.Shift_End_Time}`;
            if (!seen.has(key)) {
              seen.add(key);
              acc.push({
                value: idx,
                label: s.EmployeeName,
                Shift_Name: s.Shift_Name,
                Shift_Start_Time: s.Shift_Start_Time,
                Shift_End_Time: s.Shift_End_Time,
                FromDate: s.FromDate,
                ToDate: s.ToDate,
              });
            }
            return acc;
          }, []);

          setShifts(mappedShifts);
          setSelectedShift(null);

          // FIX 5: Auto-select the shift for the current date if employee is available on that date
          const shiftForToday = rawShifts.find((row) => {
            const s = toDateStr(currentDate);
            const from = row.FromDate?.split("T")[0];
            const to = row.ToDate?.split("T")[0] || from;
            return s >= from && s <= to;
          });

          if (shiftForToday) {
            const matchedMapped = mappedShifts.find(
              (ms) =>
                ms.Shift_Name?.trim() === shiftForToday.Shift_Name?.trim() &&
                ms.Shift_Start_Time === shiftForToday.Shift_Start_Time &&
                ms.Shift_End_Time === shiftForToday.Shift_End_Time,
            );
            if (matchedMapped) {
              setSelectedShift(matchedMapped);
              const slots = shiftToSlots(matchedMapped);
              if (
                slots &&
                !(workWeek === 5 && isWeekend(currentDate)) &&
                !isHoliday(currentDate, holidays)
              ) {
                const sel = {
                  startDate: currentDate,
                  endDate: currentDate,
                  startSlot: slots.startSlot,
                  endSlot: slots.endSlot,
                };
                setDaySelStart({ date: currentDate, slot: slots.startSlot });
                setDaySelEnd({ date: currentDate, slot: slots.endSlot });
                setDaySelStep("start");
                setSelection(sel);
                setConfirmed((prev) =>
                  prev ? { ...prev, ...sel } : { res: RESOURCES[0], ...sel },
                );
                setSelectionSource("shift");
              }
            }
          }
        }
      })
      .catch(console.error);
  }, [selectedEmployee, selectedLab, locationId, dispatch]);

  // ─── Fetch employees on lab select ────────────────────────────────────────
  useEffect(() => {
    if (!selectedLab?.facilityId) return;

    setEmployeesLoading(true);
    dispatch(
      fetchEmployeesByFacility({
        facilityId: selectedLab.facilityId,
        companyId: companyID,
      }),
    )
      .then((res) => {
        const data = res?.payload;
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data?.data)) list = data.data;
        setEmployees(
          list.map((emp) => ({
            value: emp.EmployeeId || emp.employeeId || emp.id || emp.ID,
            label:
              emp.EmployeeName ||
              emp.employeeName ||
              emp.name ||
              emp.Name ||
              `${emp.FirstName || ""} ${emp.LastName || ""}`.trim() ||
              "Unnamed Employee",
            ...emp,
          })),
        );
      })
      .catch(console.error)
      .finally(() => setEmployeesLoading(false));

    if (!selectedEmployee) {
      setShiftsLoading(true);
      dispatch(
        fetchDropdownData({
          companyId: companyID,
          term: "SHIFT",
          mappingId: 0,
        }),
      )
        .then((res) => {
          const data = res?.payload;
          if (Array.isArray(data)) setShifts(data);
          else if (Array.isArray(data?.data)) setShifts(data.data);
        })
        .catch(console.error)
        .finally(() => setShiftsLoading(false));
    }
  }, [selectedLab, dispatch]);

  // ─── Restore generic shifts when employee is deselected ──────────────────
  useEffect(() => {
    if (selectedEmployee) return;
    if (!selectedLab?.facilityId) return;
    setEmployeeShiftData([]);
    setShiftsLoading(true);
    dispatch(
      fetchDropdownData({ companyId: companyID, term: "SHIFT", mappingId: 0 }),
    )
      .then((res) => {
        const data = res?.payload;
        if (Array.isArray(data)) setShifts(data);
        else if (Array.isArray(data?.data)) setShifts(data.data);
      })
      .catch(console.error)
      .finally(() => setShiftsLoading(false));
  }, [selectedEmployee, selectedLab, dispatch]);

  useEffect(() => {
    if (!confirmed) return;

    const shiftStartTime =
      selectedShift?.Shift_Start_Time || confirmed.shiftStart;
    const shiftEndTime = selectedShift?.Shift_End_Time || confirmed.shiftEnd;

    // ✅ Use the same function the Booking Preview uses — gives correct hours
    const plannedHours = getPlannedHours(
      confirmed.startSlot,
      confirmed.startDate,
      confirmed.endSlot,
      confirmed.endDate,
      confirmed.bookableDays,
      shiftStartTime,
      shiftEndTime,
    );

    // ✅ Compute correct start datetime (same logic as Booking Preview)
    let firstStartDt;
    if (shiftStartTime && shiftEndTime) {
      const [sh, sm] = shiftStartTime.split(":").map(Number);
      firstStartDt = new Date(
        confirmed.startDate.getFullYear(),
        confirmed.startDate.getMonth(),
        confirmed.startDate.getDate(),
        sh,
        sm,
        0,
      );
    } else {
      firstStartDt = slotToDateTime(confirmed.startSlot, confirmed.startDate);
    }

    if (!firstStartDt) return;

    if (onPreviewChange) {
      onPreviewChange({
        startDateTimeStr: toLocalISOString(firstStartDt),
        durationHours: plannedHours,
      });
    }
  }, [confirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Slot click handlers ───────────────────────────────────────────────────
  const handleDaySlotClick = useCallback(
    (date, slot) => {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      if (
        new Date(date.getFullYear(), date.getMonth(), date.getDate()) <
        todayMidnight
      )
        return;
      if (daySelStep === "start") {
        setDaySelStart({ date, slot });
        setDaySelEnd(null);
        setDaySelStep("end");
        setSelectionSource("manual");
        const sel = {
          startDate: date,
          endDate: date,
          startSlot: slot,
          endSlot: slot,
        };
        setSelection(sel);
        setConfirmed({ res: RESOURCES[0], ...sel });
      } else {
        const lo = Math.min(daySelStart.slot, slot);
        const hi = Math.max(daySelStart.slot, slot);
        setDaySelEnd({ date, slot });
        setDaySelStep("start");
        const sel = {
          startDate: daySelStart.date,
          endDate: date,
          startSlot: lo,
          endSlot: hi,
        };
        setSelection(sel);
        setConfirmed({ res: RESOURCES[0], ...sel });
      }
    },
    [daySelStep, daySelStart, RESOURCES],
  );

  const handleWeekDayClick = useCallback(
    (date) => {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      if (
        new Date(date.getFullYear(), date.getMonth(), date.getDate()) <
        todayMidnight
      )
        return;
      if (weekSelStep === "start") {
        setWeekSelStart(date);
        setWeekSelEnd(null);
        setWeekSelStep("end");
        const sel = {
          startDate: date,
          endDate: date,
          startSlot: 0,
          endSlot: TOTAL_HOURS - 1,
        };
        setSelection(sel);
        setConfirmed({ res: RESOURCES[0], ...sel });
      } else {
        let start = weekSelStart;
        let end = date;
        if (end < start) [start, end] = [end, start];

        const bookableDays = [];
        const cur = new Date(start);
        while (cur <= end) {
          const isDisabled =
            (workWeek === 5 && isWeekend(cur)) || isHoliday(cur, holidays);
          if (!isDisabled) bookableDays.push(new Date(cur));
          cur.setDate(cur.getDate() + 1);
        }

        setWeekSelStart(start);
        setWeekSelEnd(end);
        setWeekSelStep("start");

        const sel = {
          startDate: start,
          endDate: end,
          startSlot: 0,
          endSlot: TOTAL_HOURS - 1,
          bookableDays,
          shiftStart: null,
          shiftEnd: null,
        };
        setSelection(sel);
        setConfirmed({ res: RESOURCES[0], ...sel });
      }
    },
    [weekSelStep, weekSelStart, holidays, RESOURCES, workWeek],
  );

  const resourceRef = useRef(RESOURCES);
  useEffect(() => {
    resourceRef.current = RESOURCES;
  }, [RESOURCES]);

  const handleMonthRangeSelect = useCallback((range) => {
    setConfirmed({ res: resourceRef.current[0], ...range });
  }, []);

  const resetAllSelections = () => {
    setSelection(null);
    setConfirmed(null);
    setFormErrors({});
    setDaySelStart(null);
    setDaySelEnd(null);
    setDaySelStep("start");
    setWeekSelStart(null);
    setWeekSelEnd(null);
    setWeekSelStep("start");
    // FIX 4: also reset month selection
    setMonthSelStart(null);
    setMonthSelEnd(null);
    setSelectionSource(null);
  };

  // Re-apply selected shift slots when navigating to a new day
  useEffect(() => {
    if (!selectedShift || viewMode !== "day") return;
    if (isWeekend(currentDate) && workWeek === 5) return;
    if (isHoliday(currentDate, holidays)) return;

    const slots = shiftToSlots(selectedShift);
    if (!slots) return;

    const sel = {
      startDate: currentDate,
      endDate: currentDate,
      startSlot: slots.startSlot,
      endSlot: slots.endSlot,
    };
    setDaySelStart({ date: currentDate, slot: slots.startSlot });
    setDaySelEnd({ date: currentDate, slot: slots.endSlot });
    setDaySelStep("start");
    setSelection(sel);
    setConfirmed((prev) =>
      prev ? { ...prev, ...sel } : { res: RESOURCES[0], ...sel },
    );
    setSelectionSource("shift");
  }, [currentDate]);

  const selectFullDay = () => {
    if (selectionSource === "fullday" && daySelStart && daySelEnd) {
      setDaySelStart(null);
      setDaySelEnd(null);
      setDaySelStep("start");
      setSelection(null);
      setConfirmed(null);
      setSelectionSource(null);
      return;
    }

    const cur = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
    );

    // Get confirmed bookings for this date (exclude Tentative and Cancelled)
    const confirmedBookings = bookings.filter((b) => {
      if (["TENB", "CANC", "CONF", "APPR"].includes(b.Status_Code)) return false;
      const bs = parseDateStr(b.Planned_Start_Date_Hrs);
      const be = parseDateStr(b.Planned_End_Date_Hrs);
      return cur.getTime() >= bs.getTime() && cur.getTime() <= be.getTime();
    });

    // Use shift range if shift is selected, else full day
    const shiftStart = 0;
    const shiftEnd = TOTAL_HOURS - 1;

    // Mark blocked slots from confirmed bookings
    const blockedSlots = new Set();
    confirmedBookings.forEach((b) => {
      const bs = parseLocalISO(b.Planned_Start_Date_Hrs);
      const be = parseLocalISO(b.Planned_End_Date_Hrs);
      const startH = bs.getHours();
      const endH =
        be.getHours() === 0 && be.getMinutes() === 0 ? 24 : be.getHours();
      for (let h = startH; h < endH; h++) blockedSlots.add(h);
    });

    // Collect free slots within shift (or full day) range
    const freeSlots = [];
    for (let h = shiftStart; h <= shiftEnd; h++) {
      if (!blockedSlots.has(h)) freeSlots.push(h);
    }

    if (freeSlots.length === 0) {
      setToast({ visible: true, message: "No available slots for this day." });
      setTimeout(() => setToast({ visible: false, message: "" }), 2500);
      return;
    }

    const firstFree = freeSlots[0];
    const lastFree = freeSlots[freeSlots.length - 1];

    setDaySelStart({ date: currentDate, slot: firstFree });
    setDaySelEnd({ date: currentDate, slot: lastFree });
    setDaySelStep("start");
    const sel = {
      startDate: currentDate,
      endDate: currentDate,
      startSlot: firstFree,
      endSlot: lastFree,
      // shiftStart: selectedShift?.Shift_Start_Time || null,
      // shiftEnd: selectedShift?.Shift_End_Time || null,
    };
    setSelection(sel);
    setConfirmed({ res: RESOURCES[0], ...sel });

    setSelectionSource("fullday");
  };
  const selectFullWeek = () => {
    if (weekSelStart && weekSelEnd) {
      setWeekSelStart(null);
      setWeekSelEnd(null);
      setWeekSelStep("start");
      setSelection(null);
      setConfirmed(null);
      return;
    }
    const allWeekDays = getCurrentCalendarWeek(currentDate);
    const bookableDays = allWeekDays.filter(
      (d) => !(workWeek === 5 && isWeekend(d)) && !isHoliday(d, holidays),
    );
    if (!bookableDays.length) return;
    if (viewMode === "week") {
      setWeekSelStart(bookableDays[0]);
      setWeekSelEnd(bookableDays[bookableDays.length - 1]);
      setWeekSelStep("start");
    }
    const getEffectiveShiftTimes = () => {
      if (selectedShift?.Shift_Start_Time && selectedShift?.Shift_End_Time) {
        return {
          shiftStart: selectedShift.Shift_Start_Time,
          shiftEnd: selectedShift.Shift_End_Time,
        };
      }
      // Fall back to first available shift from employeeShiftData
      if (employeeShiftData.length > 0) {
        const first = employeeShiftData[0];
        if (first?.Shift_Start_Time && first?.Shift_End_Time) {
          return {
            shiftStart: first.Shift_Start_Time,
            shiftEnd: first.Shift_End_Time,
          };
        }
      }
      return { shiftStart: null, shiftEnd: null };
    };

    let finalBookableDaysW = bookableDays;
    if (employeeShiftData.length > 0 && !selectedShift) {
      finalBookableDaysW = bookableDays.filter((d) =>
        isEmployeeAvailable(d, employeeShiftData),
      );
    }

    const effShiftStart =
      selectedShift?.Shift_Start_Time ||
      (finalBookableDaysW.length > 0
        ? getEmployeeShiftForDate(finalBookableDaysW[0], employeeShiftData)
            ?.Shift_Start_Time
        : null) ||
      null;
    const effShiftEnd =
      selectedShift?.Shift_End_Time ||
      (finalBookableDaysW.length > 0
        ? getEmployeeShiftForDate(finalBookableDaysW[0], employeeShiftData)
            ?.Shift_End_Time
        : null) ||
      null;

    // Build shiftSlots from effective shift times
    let effectiveShiftSlots = activeShiftSlots;
    if (!effectiveShiftSlots && effShiftStart && effShiftEnd) {
      const s = timeStrToHour(effShiftStart);
      const e = timeStrToHour(effShiftEnd);
      if (s !== null && e !== null) {
        const endSlot = e === 0 ? 23 : e <= s ? e + 24 - 1 : e - 1;
        effectiveShiftSlots = {
          startSlot: s,
          endSlot: Math.min(endSlot, TOTAL_HOURS - 1),
        };
      }
    }

    const shiftSlots = effectiveShiftSlots || {
      startSlot: 0,
      endSlot: TOTAL_HOURS - 1,
    };
    if (!finalBookableDaysW.length) return;
    const sel = {
      startDate: finalBookableDaysW[0],
      endDate: finalBookableDaysW[finalBookableDaysW.length - 1],
      startSlot: shiftSlots.startSlot,
      endSlot: shiftSlots.endSlot,
      bookableDays: finalBookableDaysW,
      shiftStart: effShiftStart,
      shiftEnd: effShiftEnd,
    };
    setSelection(sel);
    setConfirmed({ res: RESOURCES[0], ...sel });
    setSelection(sel);
    setConfirmed({ res: RESOURCES[0], ...sel });
  };

  const selectFullMonth = () => {
    if (monthSelStart && monthSelEnd) {
      setMonthSelStart(null);
      setMonthSelEnd(null);
      setSelection(null);
      setConfirmed(null);
      return;
    }

    const refDate = viewMode === "month" ? monthViewDate : currentDate;
    const year = refDate.getFullYear();
    const month = refDate.getMonth();
    const weekRows = getMonthWeekRows(year, month, workWeek);
    const allDays = weekRows.flat().filter((d) => d !== null);
    const bookableDays = allDays.filter(
      (d) => !(workWeek === 5 && isWeekend(d)) && !isHoliday(d, holidays),
    );
    if (!bookableDays.length) return;

    // FIX 4: set monthSelStart/End so MonthView calendar visually highlights the selection
    setMonthSelStart(bookableDays[0]);
    setMonthSelEnd(bookableDays[bookableDays.length - 1]);

    let finalBookableDays = bookableDays;
    if (employeeShiftData.length > 0 && !selectedShift) {
      finalBookableDays = bookableDays.filter((d) =>
        isEmployeeAvailable(d, employeeShiftData),
      );
    }

    // Get shift times from selected shift or from the first available shift day
    const effShiftStart2 =
      selectedShift?.Shift_Start_Time ||
      (finalBookableDays.length > 0
        ? getEmployeeShiftForDate(finalBookableDays[0], employeeShiftData)
            ?.Shift_Start_Time
        : null) ||
      null;
    const effShiftEnd2 =
      selectedShift?.Shift_End_Time ||
      (finalBookableDays.length > 0
        ? getEmployeeShiftForDate(finalBookableDays[0], employeeShiftData)
            ?.Shift_End_Time
        : null) ||
      null;

    let effectiveShiftSlots2 = activeShiftSlots;
    if (!effectiveShiftSlots2 && effShiftStart2 && effShiftEnd2) {
      const s = timeStrToHour(effShiftStart2);
      const e = timeStrToHour(effShiftEnd2);
      if (s !== null && e !== null) {
        const endSlot = e === 0 ? 23 : e <= s ? e + 24 - 1 : e - 1;
        effectiveShiftSlots2 = {
          startSlot: s,
          endSlot: Math.min(endSlot, TOTAL_HOURS - 1),
        };
      }
    }

    const shiftSlots = effectiveShiftSlots2 || {
      startSlot: 0,
      endSlot: TOTAL_HOURS - 1,
    };
    if (!finalBookableDays.length) return;
    const sel = {
      startDate: finalBookableDays[0],
      endDate: finalBookableDays[finalBookableDays.length - 1],
      startSlot: shiftSlots.startSlot,
      endSlot: shiftSlots.endSlot,
      bookableDays: finalBookableDays,
      shiftStart: effShiftStart2,
      shiftEnd: effShiftEnd2,
    };
    setSelection(sel);
    setConfirmed({ res: RESOURCES[0], ...sel });
    setTimeout(() => {
      bookingPreviewRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  const navigate = (delta) => {
    setSelection(null);
    setConfirmed(null);
    setFormErrors({});
    setDaySelStart(null);
    setDaySelEnd(null);
    setDaySelStep("start");
    setWeekSelStart(null);
    setWeekSelEnd(null);
    setWeekSelStep("start");
    setMonthSelStart(null);
    setMonthSelEnd(null);

    if (viewMode === "month") {
      setMonthViewDate((prev) => {
        const d = new Date(prev);
        d.setMonth(d.getMonth() + delta);
        return d;
      });
      return;
    }
    if (viewMode === "week") {
      setCurrentDate((prev) => {
        const d = new Date(prev);
        d.setDate(d.getDate() + delta * 7);
        return d;
      });
      return;
    }
    const d = new Date(currentDate);
    let steps = Math.abs(delta);
    while (steps > 0) {
      d.setDate(d.getDate() + (delta > 0 ? 1 : -1));
      if (workWeek === 7 || !isWeekend(d)) steps--;
    }
    setCurrentDate(d);
  };

  const handleDateChange = (e) => {
    // Ignore the picker's "Clear" action — keep the current date instead of Invalid Date
    if (!e.target.value) return;
    const nd = new Date(e.target.value);
    if (isNaN(nd.getTime())) return;
    setCurrentDate(nd);
    setSelection(null);
    setConfirmed(null);
    setDaySelStart(null);
    setDaySelEnd(null);
    setDaySelStep("start");
    setWeekSelStart(null);
    setWeekSelEnd(null);
    setWeekSelStep("start");
    setShowDatePicker(false);
  };

  // FIX 2: clicking the active shift again deselects it (toggle)
  const handleShiftSelect = useCallback(
    (shift) => {
      if (selectedShift?.value === shift.value) {
        setSelectedShift(null);
        setFormErrors((prev) => ({ ...prev, shift: "" }));
        setDaySelStart(null);
        setDaySelEnd(null);
        setDaySelStep("start");
        setSelection(null);
        setConfirmed(null);
        setSelectionSource(null);
        return;
      }

      setSelectedShift(shift);
      setSelectionSource("shift");
      setFormErrors((prev) => ({ ...prev, shift: "" }));

      const slots = shiftToSlots(shift);
      if (!slots) return;

      const date = currentDate;
      if ((workWeek === 5 && isWeekend(date)) || isHoliday(date, holidays))
        return;

      const sel = {
        startDate: date,
        endDate: date,
        startSlot: slots.startSlot,
        endSlot: slots.endSlot,
      };
      setDaySelStart({ date, slot: slots.startSlot });
      setDaySelEnd({ date, slot: slots.endSlot });
      setDaySelStep("start");
      setSelection(sel);
      setConfirmed((prev) =>
        prev ? { ...prev, ...sel } : { res: RESOURCES[0], ...sel },
      );
    },
    [selectedShift, currentDate, workWeek, holidays, RESOURCES],
  );

  const handleConfirm = () => {
    if (!confirmed) return;

    // Keep backward compatibility if needed
    const bookingEntries = [];
    let firstStartDt = null;
    let lastEndDt = null;

    const daysToBook =
      confirmed.bookableDays && confirmed.bookableDays.length > 0
        ? confirmed.bookableDays
        : [confirmed.startDate];

    for (const day of daysToBook) {
      let startDt, endDt;

      const shiftStartTime =
        selectedShift?.Shift_Start_Time || confirmed.shiftStart;
      const shiftEndTime = selectedShift?.Shift_End_Time || confirmed.shiftEnd;

      if (shiftStartTime && shiftEndTime) {
        const [sh, sm] = shiftStartTime.split(":").map(Number);
        const [eh, em] = shiftEndTime.split(":").map(Number);
        startDt = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          sh,
          sm,
          0,
        );
        endDt = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          eh,
          em,
          0,
        );
        if (endDt <= startDt) endDt.setDate(endDt.getDate() + 1);
      } else {
        startDt = slotToDateTime(confirmed.startSlot, day);
        endDt = slotToDateTime(confirmed.endSlot + 1, day);
      }

      if (!firstStartDt) firstStartDt = startDt;
      lastEndDt = endDt;

      const hours = (endDt - startDt) / 3600000;

      bookingEntries.push({
        resource: confirmed.res, // Add resource to each entry
        startDateTime: startDt,
        endDateTime: endDt,
        startDateTimeStr: toLocalISOString(startDt),
        endDateTimeStr: toLocalISOString(endDt),
        durationHours: Math.round(hours * 10) / 10,
        shiftId: selectedShift?.value || null,
        shiftName: selectedShift?.Shift_Name || selectedShift?.label || null,
        shiftStartTime: selectedShift?.Shift_Start_Time || null,
        shiftEndTime: selectedShift?.Shift_End_Time || null,
        employeeId: selectedEmployee?.value || null,
        employeeName: selectedEmployee?.label || null,
      });
    }

    const totalHours = bookingEntries.reduce(
      (sum, e) => sum + e.durationHours,
      0,
    );

    const proceedWithBooking = (hasOverlapOverride) => {
      if (onBookingSelect) {
        // Pass both old format (for backward compatibility) and new format
        onBookingSelect({
          // Old format properties (keep these for backward compatibility)
          resource: confirmed.res,
          startDateTime: firstStartDt,
          endDateTime: lastEndDt,
          startDateTimeStr: toLocalISOString(firstStartDt),
          endDateTimeStr: toLocalISOString(lastEndDt),
          durationHours: Math.round(totalHours * 10) / 10,
          bookableDays: confirmed.bookableDays || null,
          employeeId: selectedEmployee?.value || null,
          employeeName: selectedEmployee?.label || null,
          shiftId: selectedShift?.value || null,
          shiftName: selectedShift?.Shift_Name || selectedShift?.label || null,
          shiftStartTime: selectedShift?.Shift_Start_Time || null,
          shiftEndTime: selectedShift?.Shift_End_Time || null,
          // New format properties
          bookingEntries,
          totalHours: Math.round(totalHours * 10) / 10,
          hasOverlap: hasOverlapOverride, // <-- Passed to allow parent to show warning
        });
      }

      resetAllSelections();
      setToast({ visible: true, message: "Schedule selected!" });
      setTimeout(() => setToast({ visible: false, message: "" }), 2500);
    };

    let hasTentativeOverlap = false;
    let blockingBookings = [];

    bookingEntries.forEach((entry) => {
      bookings.forEach((b) => {
        if (!["TENB", "CONF", "APPR"].includes(b.Status_Code)) return;
        
        const bs = parseLocalISO(b.Planned_Start_Date_Hrs);
        const be = parseLocalISO(b.Planned_End_Date_Hrs);
        
        if (entry.startDateTime < be && entry.endDateTime > bs) {
          if (b.Status_Code === "TENB") {
            hasTentativeOverlap = true;
          } else {
            // It is CONF or APPR
            const dateStr = entry.startDateTimeStr.split("T")[0];
            blockingBookings.push(dateStr);
          }
        }
      });
    });

    const uniqueBlockedDates = [...new Set(blockingBookings)];

    if (uniqueBlockedDates.length > 0) {
      const datesStr = uniqueBlockedDates.join(", ");
      Swal.fire({
        title: "Already Booked",
        html: `This lab is already booked for this date: <b>${datesStr}</b>.<br/><br/>Do you still want to proceed?`,
        icon: "warning",
        iconColor: "#d97706",
        showCancelButton: true,
        buttonsStyling: false,
        background: "#ffffff",
        backdrop: "rgba(0,0,0,0.4)",
        customClass: {
          popup: 'rounded-[24px] shadow-[0_24px_64px_rgba(0,0,0,0.18)] border border-gray-100 p-6',
          title: 'text-[20px] font-bold text-gray-900 mt-4',
          htmlContainer: 'text-[14px] text-gray-600 mt-2',
          actions: 'flex gap-3 justify-center w-full mt-6',
          confirmButton: 'bg-[#08757c] text-white px-6 py-2.5 rounded-xl font-bold text-[14px] shadow-lg shadow-[#08757c]/30 hover:bg-[#005b60] transition-all',
          cancelButton: 'bg-[#f1f4f4] text-[#3e494a] px-6 py-2.5 rounded-xl font-bold text-[14px] hover:bg-[#e5e9e9] transition-all'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          proceedWithBooking(true);
        }
      });
    } else {
      proceedWithBooking(hasTentativeOverlap);
    }
  };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 60,
        }}
      >
        <div style={{ textAlign: "center", fontSize: 16, color: "#6b7280" }}>
          Loading availability...
        </div>
      </div>
    );

  const quickSelectLabel =
    viewMode === "day"
      ? "Full Day"
      : viewMode === "week"
        ? "Full Week"
        : "Full Month";
  const quickSelectFn =
    viewMode === "day"
      ? selectFullDay
      : viewMode === "week"
        ? selectFullWeek
        : selectFullMonth;

  return (
    <div
      style={{
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
        maxWidth: "100%",
        padding: "20px",
        color: "#111827",
      }}
    >
      <style>{`@keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }`}</style>

      {/* ── ROW 1: Title + controls ── */}
      <div
        style={{
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
              margin: 0,
            }}
          >
            Lab Booking
          </h2>
          <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 6px" }}>
            {viewMode === "day"
              ? daySelStep === "end"
                ? "Click an end time to complete selection"
                : "Click a time slot to start selection"
              : viewMode === "week"
                ? weekSelStep === "end"
                  ? "Click a second day to set end date"
                  : "Click a day to start selection"
                : "Click start date, then end date"}
          </p>
          <div
            style={{
              display: "flex",
              background: "#f3f4f6",
              borderRadius: 10,
              padding: 3,
              gap: 2,
              width: "fit-content",
            }}
          >
            {[5, 7].map((days) => (
              <button
                key={days}
                onClick={() => {
                  setWorkWeek(days);
                  resetAllSelections();
                }}
                style={{
                  padding: "5px 14px",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: workWeek === days ? TEAL : "transparent",
                  color: workWeek === days ? "#fff" : "#6b7280",
                  transition: "all 0.15s",
                }}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {/* ── Employee dropdown with FIX 6: clear (✕) button ── */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                color: "#9ca3af",
                letterSpacing: "0.05em",
              }}
            >
              Employee
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <div style={{ position: "relative" }}>
                <select
                  value={selectedEmployee?.value ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Compare as string so numeric AND non-numeric IDs both match
                    const emp = raw
                      ? employees.find((em) => String(em.value) === raw)
                      : null;
                    setSelectedEmployee(emp || null);
                    if (!emp) {
                      setSelectedShift(null);
                      setEmployeeShiftData([]);
                    }
                  }}
                  disabled={employeesLoading}
                  style={{
                    padding: "7px 28px 7px 10px",
                    border: `1.5px solid ${selectedEmployee ? TEAL : "#e5e7eb"}`,
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 500,
                    color: selectedEmployee ? "#111827" : "#9ca3af",
                    background: employeesLoading ? "#f9fafb" : "#fff",
                    cursor: employeesLoading ? "not-allowed" : "pointer",
                    outline: "none",
                    appearance: "none",
                    boxShadow: selectedEmployee
                      ? `0 0 0 3px ${TEAL}18`
                      : "none",
                    minWidth: 160,
                    maxWidth: 200,
                  }}
                >
                  <option value="">
                    {employeesLoading ? "Loading…" : "Select employee…"}
                  </option>
                  {employees.map((emp) => (
                    <option key={emp.value} value={String(emp.value)}>
                      {emp.label}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "#9ca3af",
                    fontSize: 11,
                  }}
                >
                  ▾
                </div>
              </div>
              {/* FIX 6: clear employee button */}
              {selectedEmployee && (
                <button
                  onClick={() => {
                    setSelectedEmployee(null);
                    setSelectedShift(null);
                    setEmployeeShiftData([]);
                    resetAllSelections();
                  }}
                  title="Clear selected employee"
                  style={{
                    padding: "0 8px",
                    border: `1.5px solid ${TEAL}55`,
                    borderRadius: 8,
                    background: "#fff",
                    color: TEAL,
                    fontSize: 13,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 34,
                    lineHeight: 1,
                    flexShrink: 0,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${TEAL}12`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* ── Shift pills — FIX 2: toggle off on re-click; FIX 3: hide on holiday/weekend day ── */}
          {!isCurrentDayHoliday && viewMode === "day" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "#9ca3af",
                  letterSpacing: "0.05em",
                }}
              >
                Shift Period
              </span>
              <div
                style={{ minHeight: 36, display: "flex", alignItems: "center" }}
              >
                {shiftsLoading ? (
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    Loading…
                  </span>
                ) : shifts.length === 0 ? (
                  <span style={{ fontSize: 12, color: "#d1d5db" }}>—</span>
                ) : (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {shifts.map((shift) => {
                      const isChecked = selectedShift?.value === shift.value;
                      return (
                        <label
                          key={shift.value}
                          style={{ cursor: "pointer" }}
                          title={
                            isChecked
                              ? "Click to deselect this shift"
                              : "Click to select this shift"
                          }
                        >
                          <input
                            type="radio"
                            name="labBookingShiftHeader"
                            value={shift.value}
                            checked={isChecked}
                            onChange={() => handleShiftSelect(shift)}
                            style={{
                              position: "absolute",
                              opacity: 0,
                              width: 0,
                              height: 0,
                            }}
                          />
                          {/* FIX 2: onClick on the div handles the already-checked (deselect) case */}
                          <div
                            onClick={(e) => {
                              if (isChecked) {
                                e.preventDefault();
                                handleShiftSelect(shift);
                              }
                            }}
                            style={{
                              padding: "5px 10px",
                              border: `1.5px solid ${isChecked ? TEAL : "#e5e7eb"}`,
                              borderRadius: 20,
                              background: isChecked ? TEAL : "#fff",
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              transition: "all 0.15s",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span style={{ fontSize: 12 }}>
                              {getShiftIcon(shift)}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: isChecked ? "#fff" : "#374151",
                              }}
                            >
                              {shift.Shift_Name?.trim() || shift.label}
                            </span>
                            {shift.Shift_Start_Time && (
                              <span
                                style={{
                                  fontSize: 10,
                                  color: isChecked
                                    ? "rgba(255,255,255,0.8)"
                                    : "#9ca3af",
                                }}
                              >
                                {shift.Shift_Start_Time.slice(0, 5)}–
                                {shift.Shift_End_Time.slice(0, 5)}
                              </span>
                            )}
                            {/* FIX 2: show ✕ inside the active pill */}
                            {isChecked && (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "rgba(255,255,255,0.8)",
                                  marginLeft: 2,
                                  lineHeight: 1,
                                }}
                              >
                                ✕
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* View toggle */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                color: "#9ca3af",
                letterSpacing: "0.05em",
              }}
            >
              View
            </span>
            <div
              style={{
                display: "flex",
                background: "#f3f4f6",
                borderRadius: 10,
                padding: 3,
              }}
            >
              {[
                { v: "day", l: "Day" },
                { v: "week", l: "Week" },
                { v: "month", l: "Month" },
              ].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => {
                    setViewMode(v);
                    resetAllSelections();
                  }}
                  style={{
                    padding: "6px 18px",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    background: viewMode === v ? TEAL : "transparent",
                    color: viewMode === v ? "#fff" : "#6b7280",
                    transition: "all 0.15s",
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── ROW 2: Toolbar — FIX 3: hide Full Day button when holiday/weekend in day view ── */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {!isCurrentDayHoliday && (
          <button
            onClick={quickSelectFn}
            style={{
              padding: "6px 14px",
              background: (
                viewMode === "day"
                  ? daySelStart && daySelEnd
                  : viewMode === "week"
                    ? weekSelStart && weekSelEnd
                    : monthSelStart && monthSelEnd
              )
                ? TEAL
                : "#fff",
              border: `1px solid ${TEAL}55`,
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              color: (
                viewMode === "day"
                  ? daySelStart && daySelEnd
                  : viewMode === "week"
                    ? weekSelStart && weekSelEnd
                    : monthSelStart && monthSelEnd
              )
                ? "#fff"
                : TEAL,
              cursor: "pointer",
            }}
          >
            {quickSelectLabel}
          </button>
        )}

        <div style={{ marginLeft: "auto", position: "relative" }}>
          <input
            type="date"
            value={toDateStr(currentDate)}
            onChange={handleDateChange}
            style={{
              position: "absolute",
              opacity: 0,
              width: 0,
              height: 0,
              pointerEvents: "none",
            }}
            ref={dateInputRef}
          />
          <button
            onClick={() => dateInputRef.current?.showPicker()}
            style={{
              padding: "6px 14px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 500,
              color: "#374151",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            📅 {fmtDateShort(currentDate)}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 7,
            padding: "6px 14px",
            fontSize: 13,
            cursor: "pointer",
            color: "#374151",
            fontWeight: 500,
          }}
        >
          ← Prev
        </button>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
          {viewMode === "month"
            ? fmtMonthYear(monthViewDate)
            : viewMode === "day"
              ? fmtDate(currentDate)
              : `${fmtDateShort(displayDates[0])} – ${fmtDateShort(displayDates[displayDates.length - 1])}`}
        </span>
        <button
          onClick={() => navigate(1)}
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 7,
            padding: "6px 14px",
            fontSize: 13,
            cursor: "pointer",
            color: "#374151",
            fontWeight: 500,
          }}
        >
          Next →
        </button>
      </div>

      {/* ── MONTH VIEW ── */}
      {viewMode === "month" && (
        <MonthView
          currentDate={monthViewDate}
          bookings={bookings}
          holidays={holidays}
          onRangeSelect={handleMonthRangeSelect}
          workWeek={workWeek}
          isEmployeeMode={isEmployeeMode}
          employeeShiftData={employeeShiftData}
          externalSelStart={monthSelStart}
          externalSelEnd={monthSelEnd}
          onSelectionChange={(start, end) => {
            setMonthSelStart(start);
            setMonthSelEnd(end);
          }}
        />
      )}

      {/* ── DAY / WEEK GANTT ── */}
      {viewMode !== "month" && (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "hidden",
            background: "#fff",
            boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
          }}
        >
          {/* ── DAY VIEW ── */}
          {viewMode === "day" && (
            <div style={{ overflowX: "auto" }}>
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  minWidth: 800,
                }}
              >
                <div
                  style={{
                    width: 120,
                    minWidth: 120,
                    padding: "10px 14px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    borderRight: "1px solid #e5e7eb",
                  }}
                >
                  Lab
                  {activeShiftSlots && (
                    <div
                      style={{
                        fontSize: 9,
                        color: TEAL,
                        fontWeight: 700,
                        marginTop: 2,
                      }}
                    >
                      {hourLabel(activeShiftSlots.startSlot)}–
                      {hourLabel(activeShiftSlots.endSlot + 1)}
                    </div>
                  )}
                </div>
                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  {activeShiftSlots && (
                    <div
                      style={{
                        display: "flex",
                        height: 4,
                        background: "#f0f0f0",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: `${(activeShiftSlots.startSlot / TOTAL_HOURS) * 100}%`,
                          width: `${((activeShiftSlots.endSlot - activeShiftSlots.startSlot + 1) / TOTAL_HOURS) * 100}%`,
                          height: "100%",
                          background: TEAL,
                          borderRadius: 2,
                          opacity: 0.7,
                        }}
                      />
                    </div>
                  )}
                  <div style={{ display: "flex" }}>
                    {Array.from({ length: TOTAL_HOURS }).map((_, h) => {
                      const inShift = activeShiftSlots
                        ? h >= activeShiftSlots.startSlot &&
                          h <= activeShiftSlots.endSlot
                        : true;
                      return (
                        <div
                          key={h}
                          style={{
                            flex: 1,
                            textAlign: "center",
                            fontSize: 9,
                            color: inShift ? "#6b7280" : "#d1d5db",
                            padding: "6px 0",
                            borderLeft: h > 0 ? "1px solid #f0f0f0" : "none",
                            fontWeight: h % 6 === 0 ? 700 : 400,
                          }}
                        >
                          {hourLabel(h)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {RESOURCES.map((res) => (
                <div
                  key={res}
                  style={{
                    display: "flex",
                    borderBottom: "1px solid #f3f4f6",
                    minWidth: 800,
                  }}
                >
                  <div
                    style={{
                      width: 120,
                      minWidth: 120,
                      padding: "0 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      borderRight: "1px solid #e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      background: "#fafafa",
                      minHeight: 64,
                    }}
                  >
                    {res}
                  </div>
                  <DayView
                    date={currentDate}
                    bookings={bookings}
                    holidays={holidays}
                    daySelStep={daySelStep}
                    daySelStart={daySelStart}
                    daySelEnd={daySelEnd}
                    onDaySlotClick={handleDaySlotClick}
                    workWeek={workWeek}
                    activeShiftSlots={activeShiftSlots}
                    isEmployeeMode={isEmployeeMode}
                    employeeShiftData={employeeShiftData}
                  />
                </div>
              ))}
            </div>
          )}

          {/* ── WEEK VIEW ── */}
          {viewMode === "week" && (
            <div style={{ overflowX: "auto" }}>
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #e5e7eb",
                  background: "#f9fafb",
                }}
              >
                <div
                  style={{
                    width: 100,
                    minWidth: 100,
                    padding: "10px 14px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    borderRight: "1px solid #e5e7eb",
                  }}
                >
                  Lab
                </div>
                {displayDates.map((d, i) => {
                  const hol = isHoliday(d, holidays);
                  const wknd = workWeek === 5 && isWeekend(d);
                  const isToday = toDateStr(new Date()) === toDateStr(d);
                  return (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        padding: "8px 4px",
                        borderRight:
                          i < displayDates.length - 1
                            ? "1px solid #e5e7eb"
                            : "none",
                        background: hol
                          ? HOLIDAY_COLOR.bg
                          : wknd
                            ? "#f9fafb"
                            : "#fff",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: isToday
                            ? TEAL
                            : hol
                              ? HOLIDAY_COLOR.text
                              : wknd
                                ? "#9ca3af"
                                : "#374151",
                        }}
                      >
                        {fmtDayHeader(d)}
                      </div>
                      {wknd && (
                        <div style={{ fontSize: 9, color: "#9ca3af" }}>off</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {RESOURCES.map((res) => (
                <div
                  key={res}
                  style={{ display: "flex", borderBottom: "1px solid #f3f4f6" }}
                >
                  <div
                    style={{
                      width: 100,
                      minWidth: 100,
                      padding: "0 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      borderRight: "1px solid #e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      background: "#fafafa",
                    }}
                  >
                    {res}
                  </div>
                  {displayDates.map((d, i) => (
                    <CompactDayCell
                      key={i}
                      date={d}
                      bookings={bookings}
                      holidays={holidays}
                      weekSelStart={weekSelStart}
                      weekSelEnd={weekSelEnd}
                      onWeekDayClick={handleWeekDayClick}
                      workWeek={workWeek}
                      isEmployeeMode={isEmployeeMode}
                      employeeShiftData={employeeShiftData}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Day view selection banner ── */}
      {viewMode === "day" && daySelStart && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 16px",
            background: "#f0fdfa",
            border: `1px solid ${TEAL}33`,
            borderLeft: `3px solid ${TEAL}`,
            borderRadius: 8,
            fontSize: 12,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {!daySelEnd ? (
            <>
              <span style={{ color: TEAL, fontWeight: 600 }}>Start:</span>{" "}
              {hourLabel(daySelStart.slot)} — click an end slot
            </>
          ) : (
            <>
              <span style={{ color: TEAL, fontWeight: 600 }}>Selected:</span>{" "}
              {hourLabel(Math.min(daySelStart.slot, daySelEnd.slot))} →{" "}
              {hourLabel(Math.max(daySelStart.slot, daySelEnd.slot) + 1)}
            </>
          )}
        </div>
      )}

      {/* ── Week selection banner ── */}
      {viewMode === "week" && weekSelStart && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 16px",
            background: "#f0fdfa",
            border: `1px solid ${TEAL}33`,
            borderLeft: `3px solid ${TEAL}`,
            borderRadius: 8,
            fontSize: 12,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {!weekSelEnd ? (
            <>
              <span style={{ color: TEAL, fontWeight: 600 }}>Start:</span>{" "}
              {fmtDateFull(weekSelStart)} — click an end date
            </>
          ) : (
            <>
              <span style={{ color: TEAL, fontWeight: 600 }}>Range:</span>{" "}
              {fmtDateFull(weekSelStart)} → {fmtDateFull(weekSelEnd)}
            </>
          )}
        </div>
      )}

      {/* Legend */}
      {viewMode !== "month" && (
        <div
          style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}
        >
          {[
            { bg: "rgba(8,117,124,0.15)", border: TEAL, label: "Selected" },
            {
              bg: STATUS_COLORS.TENB.bg,
              border: STATUS_COLORS.TENB.border,
              label: "Tentative (bookable)",
            },
            {
              bg: STATUS_COLORS.APPR.bg,
              border: STATUS_COLORS.APPR.border,
              label: "BOOKED",
            },
            {
              bg: HOLIDAY_COLOR.bg,
              border: HOLIDAY_COLOR.border,
              label: "Holiday",
            },
          ].map(({ bg, border, label }) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                color: "#6b7280",
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: bg,
                  border: `1.5px solid ${border}`,
                }}
              />
              {label}
            </div>
          ))}
        </div>
      )}

      {/* ── Booking Summary ── */}
      {confirmed && (
        <div
          ref={bookingPreviewRef}
          style={{
            marginTop: 16,
            background: "#f9fafb",
            border: `1px solid ${TEAL}33`,
            borderLeft: `3px solid ${TEAL}`,
            borderRadius: 10,
            padding: "16px 20px",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#111827",
              marginBottom: 14,
            }}
          >
            Booking Preview
          </div>

          {(() => {
            let plannedStartDt, endDt;
            if (confirmed.shiftStart && confirmed.shiftEnd) {
              const [sh, sm] = confirmed.shiftStart.split(":").map(Number);
              const [eh, em] = confirmed.shiftEnd.split(":").map(Number);
              plannedStartDt = new Date(
                confirmed.startDate.getFullYear(),
                confirmed.startDate.getMonth(),
                confirmed.startDate.getDate(),
                sh,
                sm,
                0,
              );
              endDt = new Date(
                confirmed.endDate.getFullYear(),
                confirmed.endDate.getMonth(),
                confirmed.endDate.getDate(),
                eh,
                em,
                0,
              );
            } else {
              plannedStartDt = slotToDateTime(
                confirmed.startSlot,
                confirmed.startDate,
              );
              const endHour = confirmed.endSlot + 1;
              endDt =
                endHour >= TOTAL_HOURS
                  ? new Date(
                      confirmed.endDate.getFullYear(),
                      confirmed.endDate.getMonth(),
                      confirmed.endDate.getDate(),
                      23,
                      59,
                      59,
                      0,
                    )
                  : new Date(
                      confirmed.endDate.getFullYear(),
                      confirmed.endDate.getMonth(),
                      confirmed.endDate.getDate(),
                      endHour,
                      0,
                      0,
                      0,
                    );
            }

            const plannedHours = getPlannedHours(
              confirmed.startSlot,
              confirmed.startDate,
              confirmed.endSlot,
              confirmed.endDate,
              confirmed.bookableDays,
              confirmed.shiftStart,
              confirmed.shiftEnd,
            );
            const durationStr = getDurationString(
              confirmed.startSlot,
              confirmed.startDate,
              confirmed.endSlot,
              confirmed.endDate,
              confirmed.bookableDays,
              confirmed.shiftStart,
              confirmed.shiftEnd,
            );
            const skippedCount = confirmed.bookableDays
              ? Math.round(
                  (confirmed.endDate - confirmed.startDate) / 86400000,
                ) +
                1 -
                confirmed.bookableDays.length
              : 0;

            const rows = [
              { label: "Lab", value: confirmed.res },
              { label: "Planned Start", value: fmtDateTime(plannedStartDt) },
              {
                label: "Planned Hours",
                value: `${plannedHours} hr${plannedHours !== 1 ? "s" : ""}`,
              },
              { label: "Duration", value: durationStr },
              { label: "Start", value: fmtDateTime(plannedStartDt) },
              { label: "End", value: fmtDateTime(endDt) },
            ];
            if (skippedCount > 0)
              rows.push({
                label: "Holidays Skipped",
                value: `${skippedCount} day(s) excluded from booking`,
              });

            return (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2,1fr)",
                    gap: "10px 24px",
                    marginBottom: 18,
                  }}
                >
                  {rows.map(({ label, value }) => (
                    <div key={label}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color: "#9ca3af",
                          marginBottom: 2,
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#111827",
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{ borderTop: "1px solid #e5e7eb", marginBottom: 16 }}
                />

                {(selectedEmployee || selectedShift) && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      padding: "10px 12px",
                      background: "#fff",
                      border: `1px solid ${TEAL}22`,
                      borderRadius: 8,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#9ca3af",
                        alignSelf: "center",
                      }}
                    >
                      Assigned:
                    </span>
                    {selectedEmployee && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          background: `${TEAL}12`,
                          border: `1px solid ${TEAL}33`,
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: TEAL,
                        }}
                      >
                        👤 {selectedEmployee.label}
                        <button
                          onClick={() => {
                            setSelectedEmployee(null);
                            setSelectedShift(null);
                            setEmployeeShiftData([]);
                            resetAllSelections();
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: TEAL,
                            fontSize: 12,
                            padding: 0,
                            marginLeft: 2,
                            lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    {selectedShift && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          background: `${TEAL}12`,
                          border: `1px solid ${TEAL}33`,
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: TEAL,
                        }}
                      >
                        {getShiftIcon(selectedShift)}{" "}
                        {selectedShift.Shift_Name || selectedShift.label}
                        {selectedShift.Shift_Start_Time && (
                          <span style={{ fontWeight: 400, opacity: 0.8 }}>
                            &nbsp;({selectedShift.Shift_Start_Time}–
                            {selectedShift.Shift_End_Time})
                          </span>
                        )}
                        {/* FIX 2: also clearable from summary */}
                        <button
                          onClick={() => handleShiftSelect(selectedShift)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: TEAL,
                            fontSize: 12,
                            padding: 0,
                            marginLeft: 2,
                            lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Confirm Button */}
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
      >
        <button
          onClick={handleConfirm}
          disabled={!confirmed}
          style={{
            padding: "10px 28px",
            background: confirmed ? TEAL : "#e5e7eb",
            color: confirmed ? "#fff" : "#9ca3af",
            border: "none",
            borderRadius: 9,
            fontSize: 13,
            fontWeight: 600,
            cursor: confirmed ? "pointer" : "not-allowed",
            boxShadow: confirmed ? "0 4px 12px rgba(8,117,124,0.25)" : "none",
            transition: "all 0.15s",
          }}
        >
          ✓ Confirm Schedule
        </button>
      </div>

      {/* Toast */}
      {toast.visible && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#111827",
            color: "#fff",
            padding: "10px 22px",
            borderRadius: 10,
            fontSize: 13,
            zIndex: 999,
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}
        >
          ✓ {toast.message}
        </div>
      )}
    </div>
  );
}
